import React, { useState, useEffect, useMemo } from "react";
import { Card, Form, InputGroup } from "react-bootstrap";
import {
  FiBox,
  FiClock,
  FiTruck,
  FiCheckCircle,
  FiSearch,
  FiCalendar,
  FiUser,
  FiPhone,
  FiX,
} from "react-icons/fi";
import { LuChefHat } from "react-icons/lu";
import { AiOutlineCar } from "react-icons/ai";
import { Link } from "react-router-dom";
import moment from "moment";
import axiosInstance from "../config/axios";
import { OrderGrubhubURL, CompletedOrderURL } from "../../component/config/config";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../../assets/css/deliverylist.css";

import { 
  normalizeDeliveryPartner, 
  getLatestDeliveryEventType, 
  getDeliveryStatusText, 
  isDeliveryCancelled, 
  isDeliveryRefunded, 
  isDeliveryRestricted, 
  getFirstDefinedValue, 
  getMinutesFromEtaValue, 
  getEtaMinutes, 
  getPickupMinutes, 
  getTrackingUrl 
} from "../../utils";

const OrderCard = ({ order, type }) => {
  const getAddress = (addressObj) => {
    if (!addressObj) return "N/A";
    const parts = [
      addressObj.add1,
      addressObj.add2,
      addressObj.city,
      addressObj.state,
      addressObj.zip,
    ];
    return parts.filter((p) => p).join(", ");
  };

  const user = order.userId || order.user;
  const chef = order.chefId || order.chef;

  const customerAddressObject = order.userAddress
    ? order.userAddress
    : {
      add1: user?.address1,
      add2: user?.address2,
      city: user?.city,
      state: user?.state,
      zip: user?.zipCode,
    };

  const customerAddress = getAddress(customerAddressObject);
  const chefAddress = chef?.address1 ? getAddress({ 
    add1: chef.address1,
    add2: chef.address2,
    city: chef.city,
    state: chef.state,
    zip: chef.zipCode
  }) : "N/A";

  const isCancelled = isDeliveryCancelled(order);
  const isRestricted = isDeliveryRestricted(order);
  const deliveryPartner = normalizeDeliveryPartner(order.deliveryPartner || order.deliveryVendor);

  return (
    <Card className={`order-card ${isCancelled ? "cancelled-order" : ""}`}>
      <Card.Body>
        <div className="order-card-headerr">
          {order.orderId && (
            <Card.Title style={{ fontSize: "12.5px" }}>
              ORD-{order.orderId}
            </Card.Title>
          )}
          <div className="delivery-card-meta">
            {deliveryPartner && (
              <span className={`delivery-partner-pill delivery-partner-pill--${String(deliveryPartner).replace(/\s+/g, "").toLowerCase()}`}>
                <FiTruck /> {deliveryPartner}
              </span>
            )}
            {isCancelled && (
              <span className="cancelled-status-text">Cancelled</span>
            )}
          </div>
        </div>
        <div className="order-card-header">
          {type === "awaiting" && !isCancelled && order.currentDeliveryStatus != null && (
            <span className="badge driver-eta">
              <FiClock className="me-1" style={{ marginRight: "4px" }} />
              Driver ETA: {!isRestricted && getPickupMinutes(order) != null ? `${getPickupMinutes(order)} mins` : "N/A"}
            </span>
          )}
          {type === "out-delivery" && order.currentDeliveryStatus != null && (
            <span className="badge eta-mins">
              <FiClock className="me-1" />
              ETA: {!isRestricted && getEtaMinutes(order) != null ? `${getEtaMinutes(order)} mins` : "N/A"}
            </span>
          )}
        </div>

        <div className="info-section">
          <FiUser />
          <div className="info-text-content">
            <h6>{user?.fullName || "N/A"}</h6>
            <p>{customerAddress}</p>
          </div>
        </div>

        <div className={`info-section ${!order.driverDetails ? "no-border" : ""}`}>
          <LuChefHat />
          <div className="info-text-content">
            <h6>{chef?.fullName || "N/A"}</h6>
            <p>{chefAddress}</p>
          </div>
        </div>

        {order.currentDeliveryStatus != null && (
          <div className="driver-info no-border">
            <div className="driver-icon-wrapper">
              <FiTruck size={20} />
            </div>
            <div className="driver-details-content">
              <h6>{!isRestricted && order.driverDetails?.name ? order.driverDetails.name : "N/A"}</h6>
              <p>
                <AiOutlineCar /> {!isRestricted && order.driverDetails?.delivery_method ? order.driverDetails.delivery_method : "N/A"}
              </p>
              <p>
                <FiPhone /> {!isRestricted && order.driverDetails?.phone_number ? order.driverDetails.phone_number : "N/A"}
              </p>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

const DeliveryDashboard = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState(new Date());

  useEffect(() => {
    const fetchAllOrders = async () => {
      setLoading(true);
      setError(null);

      const formattedDate = moment(selectedDate).format("MM-DD-YYYY");

      const requestBody = {
        pageSize: 1000,
        pageNumber: 1,
        deliveryStartDate: formattedDate,
        deliveryEndDate: formattedDate,
      };

      try {
        const [openOrdersResponse, deliveredOrdersResponse] = await Promise.all([
          axiosInstance().post(OrderGrubhubURL, requestBody),
          axiosInstance().post(CompletedOrderURL, requestBody),
        ]);

        const openOrders = openOrdersResponse?.data?.data || [];
        const deliveredOrders = deliveredOrdersResponse?.data?.data || [];

        const allFetchedOrders = [...openOrders, ...deliveredOrders];

        const filteredByDate = allFetchedOrders.filter((order) =>
          moment(order.scheduledDate || order.placeOrderDate || order.orderDeliveryDate).isSame(selectedDate, "day")
        );

        setOrders(filteredByDate);
      } catch (err) {
        setError("Failed to fetch orders. Please try again later.");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAllOrders();
  }, [selectedDate]);

  const filteredOrders = useMemo(() => {
    if (!searchTerm) return orders;
    const lowercasedFilter = searchTerm.toLowerCase();
    return orders.filter((order) => {
      const user = order.userId || order.user;
      const chef = order.chefId || order.chef;
      return (
        (order.orderId &&
          `ord-${order.orderId}`.toLowerCase().includes(lowercasedFilter)) ||
        (user?.fullName &&
          user.fullName.toLowerCase().includes(lowercasedFilter)) ||
        (chef?.fullName &&
          chef.fullName.toLowerCase().includes(lowercasedFilter))
      );
    });
  }, [orders, searchTerm]);

  const kanbanData = useMemo(() => {
    const notReady = [];
    const awaitingPickup = [];
    const outForDelivery = [];
    const delivered = [];

    for (const order of filteredOrders) {
      const deliveryStatus = order.currentDeliveryStatus;
      const deliveryWebhookStatus = order.deliveryWebhookStatus;

      if (typeof deliveryStatus === "string" && !isDeliveryCancelled(order) && !isDeliveryRefunded(order)) {
        awaitingPickup.push(order);
        continue;
      }

      if (isDeliveryCancelled(order)) {
        awaitingPickup.push(order);
        continue;
      }

      const latestEventType = getLatestDeliveryEventType(order);
      const lastWebhookEventType = deliveryWebhookStatus?.events?.[0]?.type;


      switch (latestEventType) {
        case "CREATED":
        case "ASSIGNED":
        case "COURIER_AT_PICKUP":
        case "UNASSIGNED":
          awaitingPickup.push(order);
          break;

        case "PICKED_UP":
        case "IN_TRANSIT":
        case "COURIER_AT_DROPOFF":
          outForDelivery.push(order);
          break;

        case "DELIVERED":
        case "PROOF_OF_DELIVERY":
          delivered.push(order);
          break;

        default:
          if (order.status === 1 || isDeliveryCancelled(order)) {
            awaitingPickup.push(order);
          } else if(isDeliveryRefunded(order) && (lastWebhookEventType === "DELIVERED" || lastWebhookEventType === "PROOF_OF_DELIVERY")){
            delivered.push(order);
          } else {
            notReady.push(order);
          }
          break;
      }
    }

    return [
      {
        title: "Orders Not Ready",
        type: "not-ready",
        icon: FiBox,
        orders: notReady,
      },
      {
        title: "Awaiting Pick-Up",
        type: "awaiting",
        icon: FiClock,
        orders: awaitingPickup,
      },
      {
        title: "Out for Delivery",
        type: "out-delivery",
        icon: FiTruck,
        orders: outForDelivery,
      },
      {
        title: "Delivered",
        type: "delivered",
        icon: FiCheckCircle,
        orders: delivered,
      },
    ];
  }, [filteredOrders]);

  if (loading) {
    return (
      <div className="text-center p-5">
        <h6>Loading Dashboard...</h6>
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger m-5">{error}</div>;
  }

  return (
    <div className="delivery-dashboard-content">
      <div className="dashboard-filters">
        <InputGroup className="search-bar-dd">
          <Form.Control
            placeholder="Search by Order ID, Customer name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm ? (
            <InputGroup.Text
              className="clear-search-btn"
              onClick={() => setSearchTerm("")}
            >
              <FiX />
            </InputGroup.Text>
          ) : <InputGroup.Text className="search-icon-wrapper">
            <FiSearch />
          </InputGroup.Text>}

        </InputGroup>

      </div>

      <div className="kanban-columns">
        {kanbanData.map((status) => (
          <div key={status.type} className="kanban-col">
            <Card className="summary-card">
              <Card.Body>
                <div className="summary-content">
                  <Card.Title>{status.title}</Card.Title>
                  <div className="summary-count">{status.orders.length}</div>
                </div>
                <div className={`summary-icon-wrapper icon-${status.type}`}>
                  <status.icon className="summary-icon" />
                </div>
              </Card.Body>
            </Card>
            <div className="order-card-container">
              {status.orders.length > 0 ? (
                status.orders.map((order) => {
                  const isRestricted = isDeliveryRestricted(order);
                  const trackingUrl = getTrackingUrl(order);
                  
                  if (trackingUrl && !isRestricted) {
                    return (
                      <a
                        key={order._id}
                        href={trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ textDecoration: "none", color: "inherit" }}
                      >
                        <OrderCard order={order} type={status.type} />
                      </a>
                    );
                  }
                  
                  return (
                    <div key={order._id}>
                        <OrderCard order={order} type={status.type} />
                    </div>
                  );
                })
              ) : (
                <div className="text-center p-3 text-muted">
                  No orders in this stage.
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DeliveryDashboard;
